import { Plus, Trash2 } from "lucide-react";
import { Button } from "../../components/ui/Button";
import { useConfirm } from "../../components/ui/ConfirmDialog";
import { EmptyState, Skeleton } from "../../components/ui/Feedback";
import { Input, Select } from "../../components/ui/Input";
import { useToast } from "../../components/ui/Toast";
import { LabelWithHint } from "../../components/ui/Tooltip";
import { majorToMinor, minorToMajor } from "../../lib/money";
import type { GiftDiscountKind, OfferRule, OfferRulePatch } from "../../lib/types";
import { SelectorField } from "./SelectorField";
import { useCreateRule, useDeleteRule, useOfferRules, useUpdateRule } from "./useOfferRules";

const REPEAT_HELP =
	"How many times one cart can earn this. Leave at 0 for no limit, so a cart of six earns it three times on a buy one get one.";

const DELIVERY_HELP =
	"Discount in cart only marks down something the shopper already added. Offer to add prompts them to pick the reward, which is what you want when the reward is a choice.";

const NEW_RULE: OfferRulePatch = {
	buySelectorKind: "product_type",
	buyValues: [],
	buyQuantity: 1,
	getSelectorKind: "product_type",
	getValues: [],
	getQuantity: 1,
	getMatchTrigger: null,
	discountKind: "free",
	discountValue: 0,
	maxApplications: 0,
	rewardDelivery: "discount_in_cart",
};

const RuleCard = ({
	rule,
	campaignId,
	index,
}: {
	rule: OfferRule;
	campaignId: string;
	index: number;
}) => {
	const update = useUpdateRule(campaignId);
	const remove = useDeleteRule(campaignId);
	const toast = useToast();
	const confirm = useConfirm();

	const notify = { onError: (error: unknown) => toast.push((error as Error).message, "error") };
	const save = (patch: OfferRulePatch) => update.mutate({ ruleId: rule.id, ...patch }, notify);

	return (
		<div className="space-y-4 rounded-md border border-line bg-surface p-4">
			<div className="flex items-center justify-between gap-3">
				<span className="text-xs font-medium text-ink">Rule {index + 1}</span>
				<Button
					variant="ghost"
					size="sm"
					aria-label="Delete rule"
					onClick={() =>
						confirm({
							title: "Delete this rule?",
							description: "Shoppers stop earning this reward after the next publish.",
							onConfirm: () =>
								remove.mutate(rule.id, {
									onSuccess: () => toast.push("Rule deleted"),
									...notify,
								}),
						})
					}
				>
					<Trash2 className="h-3.5 w-3.5" strokeWidth={1.75} />
				</Button>
			</div>

			<div className="grid gap-5 sm:grid-cols-2">
				<div className="space-y-3">
					<label className="block space-y-1.5">
						<span className="text-sm text-ink">Buy quantity</span>
						<Input
							type="number"
							min="1"
							step="1"
							defaultValue={rule.buyQuantity}
							className="max-w-24"
							onBlur={(event) => {
								const value = Math.max(1, Math.trunc(Number(event.target.value) || 1));
								if (value !== rule.buyQuantity) save({ buyQuantity: value });
							}}
						/>
					</label>
					<SelectorField
						label="Buy"
						kind={rule.buySelectorKind}
						values={rule.buyValues}
						onChange={(next) => save({ buySelectorKind: next.kind, buyValues: next.values })}
					/>
				</div>

				<div className="space-y-3">
					<label className="block space-y-1.5">
						<span className="text-sm text-ink">Reward quantity</span>
						<Input
							type="number"
							min="1"
							step="1"
							defaultValue={rule.getQuantity}
							className="max-w-24"
							onBlur={(event) => {
								const value = Math.max(1, Math.trunc(Number(event.target.value) || 1));
								if (value !== rule.getQuantity) save({ getQuantity: value });
							}}
						/>
					</label>
					<SelectorField
						label="Get"
						kind={rule.getSelectorKind}
						values={rule.getValues}
						matchTrigger={rule.getMatchTrigger}
						allowMatchTrigger
						onChange={(next) =>
							save({
								getSelectorKind: next.kind,
								getValues: next.values,
								getMatchTrigger: next.matchTrigger,
							})
						}
					/>
				</div>
			</div>

			<div className="grid gap-4 border-t border-line pt-4 sm:grid-cols-3">
				<label className="block space-y-1.5">
					<span className="text-sm text-ink">Discount</span>
					<Select
						value={rule.discountKind}
						onChange={(event) =>
							save({ discountKind: event.target.value as GiftDiscountKind, discountValue: 0 })
						}
					>
						<option value="free">Free</option>
						<option value="percentage">Percentage off</option>
						<option value="fixed_amount">Amount off</option>
					</Select>
				</label>

				{rule.discountKind === "free" ? null : (
					<label className="block space-y-1.5">
						<span className="text-sm text-ink">
							{rule.discountKind === "percentage" ? "Percent off" : "Amount off"}
						</span>
						<Input
							type="number"
							min="0"
							step={rule.discountKind === "percentage" ? "1" : "0.01"}
							defaultValue={
								rule.discountKind === "percentage"
									? String(rule.discountValue / 100)
									: minorToMajor(rule.discountValue)
							}
							onBlur={(event) =>
								save({
									discountValue:
										rule.discountKind === "percentage"
											? Math.round(Number(event.target.value || "0") * 100)
											: majorToMinor(event.target.value || "0"),
								})
							}
						/>
					</label>
				)}

				<label className="block space-y-1.5">
					<LabelWithHint label="Repeat">{REPEAT_HELP}</LabelWithHint>
					<Input
						type="number"
						min="0"
						step="1"
						defaultValue={rule.maxApplications}
						onBlur={(event) => {
							const value = Math.max(0, Math.trunc(Number(event.target.value) || 0));
							if (value !== rule.maxApplications) save({ maxApplications: value });
						}}
					/>
				</label>

				<label className="block space-y-1.5 sm:col-span-3">
					<LabelWithHint label="Reward delivery">{DELIVERY_HELP}</LabelWithHint>
					<Select
						value={rule.rewardDelivery}
						className="max-w-sm"
						onChange={(event) =>
							save({ rewardDelivery: event.target.value as OfferRule["rewardDelivery"] })
						}
					>
						<option value="discount_in_cart">Discount it if already in the cart</option>
						<option value="offer_to_add">Offer to add it</option>
					</Select>
				</label>
			</div>

			<p className="text-xs text-ink-faint">
				The cheapest qualifying item is always the one discounted.
			</p>
		</div>
	);
};

export const RuleEditor = ({ campaignId }: { campaignId: string }) => {
	const rules = useOfferRules(campaignId);
	const create = useCreateRule(campaignId);
	const toast = useToast();

	const items = rules.data?.items ?? [];

	return (
		<div className="space-y-4">
			<div className="flex flex-wrap items-center justify-between gap-3">
				<p className="max-w-md text-xs text-ink-muted">
					Each rule is one offer. Buy some quantity of one thing, get some quantity of another free
					or discounted. Rules are earned independently and stack.
				</p>
				<Button
					variant="outline"
					size="sm"
					disabled={create.isPending}
					onClick={() =>
						create.mutate(NEW_RULE, {
							onSuccess: () => toast.push("Rule added"),
							onError: (error) => toast.push(error.message, "error"),
						})
					}
				>
					<Plus className="h-3.5 w-3.5" strokeWidth={2} />
					Add rule
				</Button>
			</div>

			{rules.isPending ? <Skeleton className="h-40 w-full" /> : null}

			{rules.data && items.length === 0 ? (
				<EmptyState
					title="No rules yet"
					description="Add one to describe what a shopper has to buy and what they get for it."
				/>
			) : null}

			{items.map((rule, index) => (
				<RuleCard key={rule.id} rule={rule} campaignId={campaignId} index={index} />
			))}
		</div>
	);
};
